import Image from 'next/image'
import { cn } from '@/utils/styles/cn'
import { LOGOS } from '@/features/landing-page/landing-logo-carousel-section'

/** OMR is a white-on-black mark, so it can't render as a flat black logo like the rest — drop it. */
const LP_LOGOS = LOGOS.filter((logo) => logo.name !== 'OMR')

/**
 * Real intrinsic dimensions of each logo asset. Passing the true aspect ratio to next/image reserves
 * the correct width BEFORE the image loads — without it the placeholder box uses a wrong aspect and
 * the row visibly reflows ("massive gap → snap") once the logos load in.
 */
const LOGO_DIMENSIONS: Record<string, { width: number; height: number }> = {
    'Interface Capital': { width: 181, height: 40 },
    S16VC: { width: 127, height: 54 },
    'Kapten & Son': { width: 339, height: 41 },
    Lovable: { width: 163, height: 37 },
    HelloFresh: { width: 143, height: 49 },
    Lanch: { width: 146, height: 35 },
    ElevenLabs: { width: 248, height: 38 },
    Creandum: { width: 333, height: 44 },
}

/** One copy of the logo row. Four are rendered for the seamless `logo-scroll` (-50%) loop. */
function LogoBatch({ logoHeightClass, gapPx }: { logoHeightClass: string; gapPx: number }) {
    return (
        <div className="flex shrink-0 items-center" style={{ gap: gapPx }}>
            {LP_LOGOS.map((logo) => {
                const dimensions = LOGO_DIMENSIONS[logo.name] ?? { width: 130, height: 24 }
                return (
                    <div key={logo.name} className="flex shrink-0 items-center justify-center brightness-0">
                        <Image
                            src={logo.src}
                            alt={logo.alt}
                            width={dimensions.width}
                            height={dimensions.height}
                            className={cn('h-auto w-auto object-contain', logoHeightClass)}
                        />
                    </div>
                )
            })}
        </div>
    )
}

/**
 * Shared black-logo marquee (pure-CSS `logo-scroll`, -50% loop, cream fade edges). `logoHeightClass`
 * sets each logo's max-height; `gapPx` sets both the inter-logo gap and the seam between the two loops.
 */
export function LogoMarquee({ logoHeightClass, gapPx }: { logoHeightClass: string; gapPx: number }) {
    return (
        <div className="relative w-full overflow-hidden">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 md:w-16"
                style={{ background: 'linear-gradient(to right, hsl(var(--background)), transparent)' }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 md:w-16"
                style={{ background: 'linear-gradient(to left, hsl(var(--background)), transparent)' }}
            />

            <div
                className="flex w-max items-center will-change-transform"
                style={{ animation: 'logo-scroll 42s linear infinite' }}
            >
                {/* 4 copies: the -50% loop shifts two batches, so coverage stays gapless up to
                    ~2× a batch width (~4.5k px viewports). */}
                {Array.from({ length: 4 }, (_, index) => (
                    <div key={index} className="flex shrink-0 items-center">
                        <LogoBatch logoHeightClass={logoHeightClass} gapPx={gapPx} />
                        <div className="shrink-0" style={{ width: gapPx }} aria-hidden />
                    </div>
                ))}
            </div>
        </div>
    )
}
